import { Controller } from 'stimulus';

export default class extends Controller {
  // This controller can be used to control a checkbox elements based on the
  // value of a radio button. The radio button must call the toggle method
  // when a change is detected. The checkbox must have a data attribute
  // called `data-radio-toggled-checkbox-condition` with a JSON value like
  // {name: 'input_name', value: 'radio_value_1,radio_value_2'} that contains the value(s)
  // of the radio button that will enable the checkbox.
  // A similar attribute, `data-radio-toggled-checkbox-checked-condition`, can be used
  // to specify radio button value(s) that should automatically check the checkbox.
  // Example: app/views/organisations/admin/organisation_users/shared/_feature_attributes.html.haml
  // paired with app/views/organisations/admin/organisation_users/shared/_role_radio_buttons.html.haml
  // Can be used with the keepCheckedValue attribute to keep some checkboxes checked.
  static targets = ['checkbox'];
  static values = {
    keepChecked: Array,
  };

  toggle({ target: { value: selectedValue } }) {
    this.checkboxTargets.forEach((checkbox) => {
      // --- Enable/Disable Logic ---
      const enableConditionAttr =
        checkbox.dataset.radioToggledCheckboxCondition;
      let isEnabled = true; // Default to enabled if no condition specified
      if (enableConditionAttr) {
        try {
          const enableCondition = JSON.parse(enableConditionAttr);
          // Check if the value property exists and includes the selected radio value
          // Assumes enableCondition.value is a comma-separated string like "1,2,3"
          if (Object.hasOwn(enableCondition, 'value')) {
            isEnabled = enableCondition.value
              .split(',')
              .map((s) => s.trim())
              .includes(selectedValue);
          }
        } catch (e) {
          console.error(
            'Error parsing radioToggledCheckboxCondition JSON:',
            enableConditionAttr,
            e
          );
          isEnabled = false; // Disable on error
        }
      }

      // Disable checkbox if it's not enabled
      checkbox.disabled = !isEnabled;

      // --- Auto-Check Logic (Optional Attribute) ---
      const checkedConditionAttr =
        checkbox.dataset.radioToggledCheckboxCheckedCondition;
      let forceCheck = false; // Determine if the condition forces a check
      if (checkedConditionAttr) {
        try {
          const checkedCondition = JSON.parse(checkedConditionAttr);
          // Check if the value property exists and includes the selected radio value
          // Assumes checkedCondition.value is a comma-separated string like "1,2,3"
          if (Object.hasOwn(checkedCondition, 'value')) {
            forceCheck = checkedCondition.value
              .split(',')
              .map((s) => s.trim())
              .includes(selectedValue);
          }
        } catch (e) {
          console.error(
            'Error parsing radioToggledCheckboxCheckedCondition JSON:',
            checkedConditionAttr,
            e
          );
          // Reset flag on error
          forceCheck = false;
        }
      }

      // --- Set Checked State ---
      if (forceCheck) {
        // If the checked condition attribute exists and its value matches the selected radio, force check ON.
        checkbox.checked = true;
      } else {
        // Otherwise (checked condition attribute doesn't exist, or exists but doesn't match),
        // then uncheck it ONLY IF it's disabled AND not in the keepCheckedValue list.
        // Otherwise, leave it as is (respecting user interaction or keepCheckedValue).
        if (!isEnabled && !this.keepCheckedValue.includes(checkbox.value)) {
          checkbox.checked = false;
        }
        // If isEnabled is true, or if !isEnabled but keepCheckedValue includes the value,
        // the checked state remains unchanged in this 'else' block.
      }
    });

    this.element
      .querySelectorAll('[data-visible-for-role-ids]')
      .forEach((el) => {
        const visibleIds = el.dataset.visibleForRoleIds
          .split(',')
          .filter(Boolean);
        el.hidden =
          visibleIds.length > 0 && !visibleIds.includes(selectedValue);
      });
  }
}
